import React, { Dispatch, useContext } from 'react';
import { FormControl, Input, InputLabel, Button, Box, Typography } from '@mui/material';
import { DICTIONARY } from 'shared/constants';
import {
  confirmSignIn,
  getCurrentUser,
  fetchUserAttributes,
  sendUserAttributeVerificationCode,
} from 'aws-amplify/auth';
import UserContext, { APP_AUTH_STATES } from 'shared/contexts/UserContext';
import AuthService from 'services/auth';
import NationalService from 'services/national';
import PersonService from 'services/person';
import { Role } from 'API';

interface Props {
  dispatch: Dispatch<LoginAction>;
  state: LoginState;
}

const ConfirmNewPassword: React.FC<Props> = ({ dispatch, state }: Props) => {
  const [, userDispatch] = useContext(UserContext);

  const { challengeError, newPassword } = state;

  const handleChallengeSubmit = async () => {
    dispatch({ type: 'loading' });

    try {
      const { isSignedIn } = await confirmSignIn({ challengeResponse: newPassword });

      dispatch({ type: 'loading' });

      if (!isSignedIn) return;

      // Check email verification after challenge completion
      const attributes = await fetchUserAttributes();

      if (attributes.email_verified !== 'true') {
        await sendUserAttributeVerificationCode({ userAttributeKey: 'email' });
        dispatch({ type: 'verification prompt' });

        return;
      }

      const currentUser = await getCurrentUser();
      const person = await PersonService.getCurrent();
      const role = await AuthService.getRoleForCurrentUser();
      const {
        accessCompanies, adminCompanies,
      } = await AuthService.getCompaniesCurrentUserCanAccess();
      const dependents = role === Role.FOREIGNNATIONAL
        ? NationalService.getDependentIDs(person) : [];

      if (person) {
        PersonService.setPersonID(person.id);
        PersonService.setPersonType(person.__typename);
      }

      userDispatch({
        type: 'login',
        authState: APP_AUTH_STATES.SignedIn,
        authData: currentUser,
        person,
        role,
        accessCompanies,
        adminCompanies,
        dependents,
      });
    } catch (error: any) {
      dispatch({ type: 'challenge error', error: error.message });
    }
  };

  return (
    <>
      <Typography sx={{ marginBottom: '2rem' }} variant="h5" component="h1">
        {DICTIONARY.SetNewPassword}
      </Typography>

      {challengeError && (
        <Typography className="error-text">{challengeError}</Typography>
      )}

      <form onSubmit={handleChallengeSubmit}>
        <FormControl sx={{ marginBottom: '2rem', width: '100%' }}>
          <InputLabel htmlFor="new-password-input">
            {DICTIONARY.NewPassword}
          </InputLabel>
          <Input
            id="new-password-input"
            value={newPassword}
            onChange={(e) => dispatch({
              type: 'field',
              fieldName: 'newPassword',
              payload: e.currentTarget.value.trim(),
            })}
            type="password"
          />
        </FormControl>

        <Box display="flex">
          <Button
            sx={{ marginLeft: 'auto' }}
            onClick={handleChallengeSubmit}
            color="primary"
            variant="contained"
          >
            {DICTIONARY.ResetPassword}
          </Button>
        </Box>
      </form>
    </>
  );
};

export default ConfirmNewPassword;
